import { Loader2 } from "lucide-react";
import { APP_NAME, getAppBranding, getCurrentTenant } from "@/lib/appMeta";
import { TENANT_BRANDING } from "@/lib/tenantBranding";
import { useTheme } from "@/hooks/use-theme";
import { cn } from "@/lib/utils";

/**
 * Branded full-screen splash — same logo pulse as the static boot shell in
 * index.html, used while the session is being restored on refresh. Unlike
 * BrandLogo, a tenant never overrides this mark or the favicon — only the
 * sidebar shows the tenant's own logo.
 */
export function AppBootSplash({ className = "" }) {
  const branding = getAppBranding();
  const tenantBranding = TENANT_BRANDING[getCurrentTenant()];
  const { isDark } = useTheme();
  const logo = isDark && branding.loaderLogoDark ? branding.loaderLogoDark : branding.loaderLogo;
  const label = tenantBranding ? `${tenantBranding.label} ${APP_NAME}` : APP_NAME;

  return (
    <div
      role="status"
      aria-label={`Loading ${label}`}
      className={cn("app-boot min-h-svh flex-1", className)}
    >
      <img src={logo} alt="" height={40} data-brand-ready />
    </div>
  );
}

/**
 * Full-height loading state for route transitions and session restore.
 * `role="status"` so a screen reader announces it without stealing focus.
 */
export function AppLoading({ label = "Loading" }) {
  return (
    <div
      role="status"
      className="flex min-h-svh flex-1 flex-col items-center justify-center gap-3 bg-background p-8"
    >
      <Loader2 className="size-5 animate-spin text-muted-foreground" aria-hidden="true" />
      <p className="text-sm text-muted-foreground">{label}…</p>
    </div>
  );
}

/** Same idea, but sized for a panel inside the app shell. */
export function PanelLoading({ label = "Loading" }) {
  return (
    <div
      role="status"
      className="flex flex-1 items-center justify-center gap-2 p-8 text-sm text-muted-foreground"
    >
      <Loader2 className="size-4 animate-spin" aria-hidden="true" />
      <span>{label}…</span>
    </div>
  );
}
